import {UseFormReturn} from 'react-hook-form';

import {VehicleTypeEnumObject} from '@dms/api/shared';
import {ConditionEnum, FuelTypeEnum, VinDecoderResponseBody} from '@dms/api/vehicle';
import {yearOptionsWithFuture} from '@dms/shared';

import {getApiDateString} from 'shared/web';

import {BaseVehicle} from '../../types/BaseVehicle';

export interface FormUpdateActionParams {
  formApi: UseFormReturn<BaseVehicle>;
  decodedData?: VinDecoderResponseBody;
}

// modelYear is rendered as a select, so a decoded value outside the offered years would only blank it.
const isSelectableYear = (year: string) =>
  yearOptionsWithFuture.some((option) => option.value === year);

/**
 * Updates the form with data from VIN decoding.
 */
// eslint-disable-next-line complexity, max-statements -- Pre-existing violation
export const formUpdateFromVinAction = (
  _context: unknown,
  params: FormUpdateActionParams
): void => {
  const {formApi, decodedData} = params;

  if (!decodedData) {
    return;
  }

  const {
    make,
    modelFamily,
    vehicleType,
    trim,
    variant,
    firstRegistrationOnYear,
    firstRegistrationOnMonth,
    firstRegistrationOnDay,
    manufacturedOnYear,
    manufacturedOnMonth,
    modelYear,
    bodyStyle,
    fuelType,
    transmission,
    drive,
    engineCode,
    power,
    engineVolume,
    secondaryFuelType,
    exteriorColor,
    manufacturerColorName,
    manufacturerColorCode,
    interiorColorName,
    interiorColorCode,
    weight,
    maximalPower,
    seatCount,
    fuelConsumptionUrban,
    fuelConsumptionCombined,
    fuelConsumptionExtraUrban,
    carbonDioxideEmission,
    emissionClass,
    doorCount,
    condition,
    isRegistered,
    dimensions,
    otherRecords,
    features,
    registrationCertificateNumber,
    technicalInspectionValidUntil,
  } = decodedData;

  // Basic Information
  if (make) {
    formApi.setValue('formMake', make);
    formApi.setValue('make', make);
    formApi.setValue('customMake', null);
  }

  if (modelFamily) {
    formApi.setValue('formModelFamily', modelFamily);
    formApi.setValue('modelFamily', modelFamily);
    formApi.setValue('customModelFamily', null);
  }

  if (vehicleType) {
    formApi.setValue('type', [vehicleType as VehicleTypeEnumObject]);
  }

  if (trim) {
    formApi.setValue('trim', trim);
  }

  if (variant) {
    formApi.setValue('variant', variant);
  }

  // Registration Information
  if (firstRegistrationOnYear && firstRegistrationOnMonth && firstRegistrationOnDay) {
    formApi.setValue(
      'firstRegistrationOn',
      getApiDateString(
        // eslint-disable-next-line eag/no-native-date-math -- Pre-existing native Date math, migrate to date-fns when possible
        new Date(
          parseInt(firstRegistrationOnYear, 10),
          parseInt(firstRegistrationOnMonth, 10) - 1, // Month is 0-indexed in JS Date
          parseInt(firstRegistrationOnDay, 10)
        )
      )
    );
  }

  if (isRegistered !== undefined && isRegistered !== null) {
    formApi.setValue('isRegistered', isRegistered);
  }

  // Manufacturing Information
  if (manufacturedOnYear) {
    formApi.setValue('manufacturedOnYear', manufacturedOnYear);
  }

  if (manufacturedOnMonth) {
    // Ensure it's a valid month string (1-12)
    const monthStr = manufacturedOnMonth.toString().trim();
    if (monthStr && /^\d{1,2}$/.test(monthStr)) {
      const monthNum = parseInt(monthStr, 10);
      if (monthNum >= 1 && monthNum <= 12) {
        formApi.setValue('manufacturedOnMonth', monthNum.toString());
      }
    }
  }

  if (modelYear) {
    const modelYearStr = modelYear.toString().trim();
    if (isSelectableYear(modelYearStr)) {
      formApi.setValue('modelYear', modelYearStr);
    }
  }

  // Physical Characteristics
  if (bodyStyle) {
    formApi.setValue('bodyStyle', [bodyStyle]);
  }

  if (doorCount) {
    const doorCountStr = doorCount.toString().trim();
    if (doorCountStr && /^\d+$/.test(doorCountStr)) {
      const parsedDoorCount = parseInt(doorCountStr, 10);
      if (!Number.isNaN(parsedDoorCount) && parsedDoorCount > 0) {
        formApi.setValue('doorCountCategory', [parsedDoorCount.toString()]);
      }
    }
  }

  if (seatCount) {
    // Handle both string and numeric seat count values
    const seatCountStr = seatCount.toString().trim();
    if (seatCountStr && /^\d+$/.test(seatCountStr)) {
      const parsedSeatCount = parseInt(seatCountStr, 10);
      if (!Number.isNaN(parsedSeatCount) && parsedSeatCount > 0) {
        formApi.setValue('seatCount', [parsedSeatCount.toString()]);
      }
    }
  }

  // Appearance
  if (exteriorColor) {
    formApi.setValue('exteriorColor', exteriorColor);
  }

  if (manufacturerColorName) {
    formApi.setValue('manufacturerColorName', manufacturerColorName);
  }

  if (manufacturerColorCode) {
    formApi.setValue('manufacturerColorCode', manufacturerColorCode);
  }

  if (interiorColorName) {
    formApi.setValue('interiorColorName', interiorColorName);
  }

  if (interiorColorCode) {
    formApi.setValue('interiorColorCode', interiorColorCode);
  }

  // Engine & Performance
  if (fuelType) {
    formApi.setValue('fuelType', [fuelType as FuelTypeEnum]);
  }

  if (secondaryFuelType) {
    formApi.setValue('secondaryFuelType', [secondaryFuelType as FuelTypeEnum]);
  }

  if (transmission) {
    formApi.setValue('transmission', [transmission]);
  }

  if (drive) {
    formApi.setValue('drive', [drive]);
  }

  if (engineCode) {
    formApi.setValue('engineCode', engineCode);
  }

  if (power) {
    const parsedPower = parseInt(power, 10);
    if (!Number.isNaN(parsedPower)) {
      formApi.setValue('power', parsedPower);
    }
  }

  if (maximalPower) {
    const parsedMaximalPower = parseInt(maximalPower, 10);
    if (!Number.isNaN(parsedMaximalPower)) {
      formApi.setValue('maximalPower', parsedMaximalPower);
    }
  }

  if (engineVolume) {
    const parsedEngineVolume = parseInt(engineVolume, 10);
    if (!Number.isNaN(parsedEngineVolume)) {
      formApi.setValue('engineVolume', parsedEngineVolume);
    }
  }

  // Weight & Dimensions
  if (weight) {
    const parsedWeight = parseInt(weight, 10);
    if (!Number.isNaN(parsedWeight)) {
      formApi.setValue('weight', parsedWeight);
    }
  }

  // Additional weight and wheelbase fields from dimensions object
  if (dimensions) {
    if (dimensions.operatingWeight) {
      const parsedOperatingWeight = parseInt(dimensions.operatingWeight.toString(), 10);
      if (!Number.isNaN(parsedOperatingWeight)) {
        formApi.setValue('operatingWeight', parsedOperatingWeight);
      }
    }

    if (dimensions.maximalLoadCapacity) {
      const parsedMaxLoadCapacity = parseInt(dimensions.maximalLoadCapacity.toString(), 10);
      if (!Number.isNaN(parsedMaxLoadCapacity)) {
        formApi.setValue('maximalLoadCapacity', parsedMaxLoadCapacity);
      }
    }

    if (dimensions.wheelBase) {
      const parsedWheelBase = parseInt(dimensions.wheelBase.toString(), 10);
      if (!Number.isNaN(parsedWheelBase)) {
        formApi.setValue('wheelBase', parsedWheelBase);
      }
    }
  }

  if (dimensions) {
    const dimensionData = {
      width: dimensions.width || null,
      height: dimensions.height || null,
      length: dimensions.length || null,
    };

    // Only set dimensions if at least one value is valid
    const hasValidDimensions = Object.values(dimensionData).some((value) => value !== null);
    if (hasValidDimensions) {
      formApi.setValue('dimensions', dimensionData);
    }
  }

  // Consumption & Emissions
  if (fuelConsumptionUrban) {
    const parsedConsumption = parseFloat(fuelConsumptionUrban);
    if (!Number.isNaN(parsedConsumption)) {
      formApi.setValue('fuelConsumptionUrban', parsedConsumption.toString());
    }
  }

  if (fuelConsumptionCombined) {
    const parsedConsumption = parseFloat(fuelConsumptionCombined);
    if (!Number.isNaN(parsedConsumption)) {
      formApi.setValue('fuelConsumptionCombined', parsedConsumption.toString());
    }
  }

  if (fuelConsumptionExtraUrban) {
    const parsedConsumption = parseFloat(fuelConsumptionExtraUrban);
    if (!Number.isNaN(parsedConsumption)) {
      formApi.setValue('fuelConsumptionExtraUrban', parsedConsumption.toString());
    }
  }

  if (carbonDioxideEmission) {
    const parsedEmission = parseFloat(carbonDioxideEmission);
    if (!Number.isNaN(parsedEmission)) {
      formApi.setValue('carbonDioxideEmission', parsedEmission.toString());
    }
  }

  if (emissionClass) {
    formApi.setValue('emissionClass', emissionClass);
  }

  // Condition
  if (condition) {
    formApi.setValue('condition', [condition as ConditionEnum]);
  }

  // Features
  if (features && Array.isArray(features)) {
    formApi.setValue('features', features);
  }

  // Additional Information
  if (otherRecords) {
    formApi.setValue('otherRecords', otherRecords);
  }

  // Registration Certificate Information
  if (registrationCertificateNumber) {
    formApi.setValue('registrationCertificateNumber', registrationCertificateNumber);
  }

  if (technicalInspectionValidUntil) {
    formApi.setValue('technicalInspectionValidUntil', technicalInspectionValidUntil);
  }

  formApi.clearErrors();
};
